import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { checkInsApi } from '../../services/api';
import type { StaffCheckIn } from '../../types';
import { formatCenterTime } from '../../utils/geolocation';
import { Button } from '../ui';
import CheckInDialog from './CheckInDialog';

// Teachers and assistants are asked to check in whenever they open the app without a check-in today.
const PROMPTED_ROLES = ['TEACHER', 'ASSISTANT'];

export default function CheckInButton() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [latest, setLatest] = useState<StaffCheckIn | null>(null);

    const userId = user?.id;
    const role = user?.role;

    // Keyed on identity, not the user object: editing one's profile must not re-open the prompt.
    useEffect(() => {
        if (!userId) return;
        let active = true;
        checkInsApi.getToday()
            .then(response => {
                if (!active) return;
                const checkIns = response.data.data.checkIns;
                setLatest(checkIns[0] ?? null);
                if (checkIns.length === 0 && role && PROMPTED_ROLES.includes(role)) {
                    setOpen(true);
                }
            })
            .catch(() => {
                // The button still works; the automatic prompt is a convenience.
            });
        return () => {
            active = false;
        };
    }, [userId, role]);

    const label = latest
        ? `Đã điểm danh tại ${latest.campus.name} lúc ${formatCenterTime(latest.checkedInAt)}. Nhấn để điểm danh lại`
        : 'Điểm danh tới cơ sở';

    return (
        <>
            <Button
                type="button"
                variant="secondary"
                size="icon"
                className="bg-white"
                icon={<MapPin className={latest ? 'h-4 w-4 text-success-600' : 'h-4 w-4 text-ink-600'} />}
                aria-label={label}
                title={label}
                onClick={() => setOpen(true)}
            />
            <CheckInDialog
                open={open}
                onClose={() => setOpen(false)}
                onCheckedIn={checkIn => setLatest(checkIn)}
            />
        </>
    );
}
